import React, { useState, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Button,
  TextField,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const InternalAccessManager = forwardRef(
  (
    {
      userEmail,
      formData,
      setFormData,
      allUsersList,
      advertiserMetaDetails = [],
      onAdvertiserAdded,
      currentAccessData = {},
      currentUserAdvertisers = []
    },
    ref
  ) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [addPlatform, setAddPlatform] = useState('DSP');
    const [selectedAdvertisersWithTimezone, setSelectedAdvertisersWithTimezone] = useState([]);
    const [removedAdvertisers, setRemovedAdvertisers] = useState([]);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      addRemovedAdvertiser: (advertiserName, timezone) => {
        // Add the advertiser back to removed advertisers list so it appears in available list
        setRemovedAdvertisers((prev) => [...prev, { name: advertiserName, timezone }]);
      },
      addRemovedOrganization: (orgName) => {
        // Organizations are handled at a different level, but we can log this
      }
    }));

    // Get current user's direct advertisers
    const getCurrentUserAdvertisers = () => {
      const currentUser = allUsersList.find((u) => u.email_id === userEmail);

      if (currentUser) {
        const directAdvertisers = [
          ...(currentUser.advertisers_utc || []),
          ...(currentUser.advertisers_non_utc || []),
          ...(currentUser.advertisers_ctv || []),
          ...(currentUser.advertisers_gtv || [])
        ];
        const seen = new Set();
        return directAdvertisers.filter((a) => {
          const k = a.id || a.name;
          if (!k || seen.has(k)) return false;
          seen.add(k);
          return true;
        });
      }
      return [];
    };

    // Get advertisers from meta details based on user's org_utc
    const getAdvertisersFromMetaDetails = () => {
      const currentUser = allUsersList.find((u) => u.email_id === userEmail);
      if (!currentUser || !currentUser.org_utc) {
        return [];
      }

      const metaAdvertisers = advertiserMetaDetails
        .filter((meta) => currentUser.org_utc.includes(meta.org_name))
        .map((advertiser) => ({
          name: advertiser.advertiser_name,
          id: advertiser.advertiser_guid,
          sfId: advertiser.salesforce_account_id,
          org_name: advertiser.org_name,
          start_date: advertiser.start_date
        }));

      return metaAdvertisers;
    };

    // Get all accessible advertisers for internal user
    const getAvailableAdvertisers = () => {
      // Internal users can only see advertisers from their own advertisers list
      // Get from get_user_advertiser_details API response (not from get_all_users_list)
      const availableAdvertisers = [...currentUserAdvertisers];

      console.log('🔍 InternalAccessManager - currentUserAdvertisers prop:', currentUserAdvertisers);
      console.log('🔍 InternalAccessManager - availableAdvertisers:', availableAdvertisers);

      const currentAccessAdvertisersUTC = currentAccessData.advertisers_utc || [];
      const currentAccessAdvertisersJST = currentAccessData.advertisers_non_utc || [];
      const currentAccessAdvertisersCTV = currentAccessData.advertisers_ctv || [];
      const currentAccessAdvertisersGTV = currentAccessData.advertisers_gtv || [];

      const filteredMainAdvertisers = availableAdvertisers.filter((advertiser) => {
        const inUtc = currentAccessAdvertisersUTC.some((adv) => (adv.id || adv.name) === advertiser.id);
        const inJst = currentAccessAdvertisersJST.some((adv) => (adv.id || adv.name) === advertiser.id);
        const inCtv = currentAccessAdvertisersCTV.some((adv) => (adv.id || adv.name) === advertiser.id);
        const inGtv = currentAccessAdvertisersGTV.some((adv) => (adv.id || adv.name) === advertiser.id);
        if (addPlatform === 'CTV') {
          return !inCtv;
        }
        if (addPlatform === 'GTV') {
          return !inGtv;
        }
        return !inUtc && !inJst;
      });

      console.log('🔍 InternalAccessManager - filteredMainAdvertisers:', filteredMainAdvertisers);

      // Then, add removed advertisers only if they don't already exist in the main list
      const finalAvailableAdvertisers = [...filteredMainAdvertisers];

      removedAdvertisers.forEach((removedAdv) => {
        // Check if this removed advertiser already exists in the main list by guid or name
        const alreadyExists = finalAvailableAdvertisers.some(
          (existing) =>
            existing.name === removedAdv.name ||
            existing.id === removedAdv.name ||
            existing.advertiser_guid === removedAdv.name ||
            (existing.sfId && existing.sfId === removedAdv.name)
        );

        if (!alreadyExists) {
          finalAvailableAdvertisers.push({
            name: removedAdv.name,
            id: removedAdv.name,
            sfId: '',
            org_name: ''
          });
        }
      });

      console.log('🔍 InternalAccessManager - finalAvailableAdvertisers:', finalAvailableAdvertisers);
      return finalAvailableAdvertisers;
    };

    const availableAdvertisers = getAvailableAdvertisers();

    // Handle advertiser selection with timezone
    const handleTimezoneSelect = (advertiser, timezone, platform = 'DSP') => {
      // Check if internal user can modify this advertiser
      if (advertiser.canModify === false) {
        console.warn(`Internal user cannot modify advertiser ${advertiser.name} - no access to organization ${advertiser.org_name}`);
        return; // Prevent adding advertisers the user can't modify
      }

      // Add to selected advertisers
      setSelectedAdvertisersWithTimezone((prev) => [...prev, { ...advertiser, timezone, platform }]);

      // Update form data
      const currentAdvertisers = formData.advertiserAccess
        ? formData.advertiserAccess.split(', ').filter((name) => name.trim() !== '')
        : [];
      const updatedAdvertisers = [...currentAdvertisers, advertiser.name];

      setFormData((prev) => ({
        ...prev,
        advertiserAccess: updatedAdvertisers.join(', ')
      }));

      // Remove from removed advertisers list since it's now added back
      setRemovedAdvertisers((prev) => prev.filter((adv) => adv.name !== advertiser.name));

      // Notify parent component about the new advertiser
      if (onAdvertiserAdded) {
        onAdvertiserAdded(advertiser, timezone, platform);
      }
    };

    // Handle removing existing advertisers
    const handleRemoveExistingAdvertiser = (advertiserName, timezone) => {
      // Remove from selected advertisers
      setSelectedAdvertisersWithTimezone((prev) => prev.filter((adv) => !(adv.name === advertiserName && adv.timezone === timezone)));

      // Update form data
      const currentAdvertisers = formData.advertiserAccess
        ? formData.advertiserAccess.split(', ').filter((name) => name.trim() !== '')
        : [];
      const updatedAdvertisers = currentAdvertisers.filter((name) => name !== advertiserName);

      setFormData((prev) => ({
        ...prev,
        advertiserAccess: updatedAdvertisers.join(', ')
      }));

      // Check if internal user has access to this advertiser
      const hasAccess = currentUserAdvertisers.some((adv) => adv.name === advertiserName || adv.advertiser_name === advertiserName);
      if (hasAccess) {
        setRemovedAdvertisers((prev) => [...prev, { name: advertiserName, timezone }]);
      }
    };

    // Filter available advertisers based on search
    const filteredAvailableAdvertisers = useMemo(() => {
      if (!searchTerm.trim()) {
        return availableAdvertisers;
      }

      const searchLower = searchTerm.toLowerCase();
      return availableAdvertisers.filter(
        (advertiser) =>
          advertiser.name.toLowerCase().includes(searchLower) || (advertiser.sfId && advertiser.sfId.toLowerCase().includes(searchLower))
      );
    }, [availableAdvertisers, searchTerm, addPlatform]);

    return (
      <Grid item xs={12}>
        {/* Available Advertisers Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" color="textPrimary" sx={{ mb: 2 }}>
            Available Advertisers - Select platform, then timezone when adding
          </Typography>

          <Box sx={{ mb: 2, maxWidth: 280 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="internal-access-platform-label">Platform</InputLabel>
              <Select
                labelId="internal-access-platform-label"
                label="Platform"
                value={addPlatform}
                onChange={(e) => setAddPlatform(e.target.value)}
              >
                <MenuItem value="DSP">DSP</MenuItem>
                <MenuItem value="CTV">CTV</MenuItem>
                <MenuItem value="GTV">GTV</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Search Bar */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search advertisers by name or SF ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ maxWidth: 400 }}
            />
          </Box>

          {/* Advertisers Table */}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 2,
              overflow: 'hidden',
              maxHeight: '400px',
              overflowY: 'auto'
            }}
          >
            {/* Table Header */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 1fr',
                gap: 2,
                p: 2,
                bgcolor: 'grey.100',
                borderBottom: '1px solid',
                borderColor: 'grey.300',
                fontWeight: 'bold',
                position: 'sticky',
                top: 0,
                zIndex: 1
              }}
            >
              <Typography variant="subtitle2">Advertiser Name</Typography>
              <Typography variant="subtitle2">SF ID</Typography>
              <Typography variant="subtitle2">ORG</Typography>
              <Typography variant="subtitle2">Action</Typography>
            </Box>

            {/* Table Body */}
            {filteredAvailableAdvertisers.length > 0 ? (
              filteredAvailableAdvertisers.map((advertiser, index) => {
                const canModify = advertiser.canModify !== false; // Default to true if not specified
                const isCurrentAccess = advertiser.isCurrentAccess === true;

                return (
                  <Box
                    key={`${advertiser.name}-${index}`}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr 1fr',
                      gap: 2,
                      p: 2,
                      borderBottom: '1px solid',
                      borderColor: 'grey.200',
                      alignItems: 'center',
                      opacity: canModify ? 1 : 0.6,
                      bgcolor: isCurrentAccess ? 'grey.50' : 'transparent',
                      '&:hover': {
                        bgcolor: canModify ? 'grey.50' : 'grey.50'
                      }
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: '500',
                        color: canModify ? 'text.primary' : 'text.secondary'
                      }}
                    >
                      {advertiser.name}
                      {isCurrentAccess && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{
                            ml: 1,
                            color: 'text.secondary',
                            fontStyle: 'italic'
                          }}
                        >
                          (Current Access)
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="body2" color={canModify ? 'textSecondary' : 'text.disabled'}>
                      {advertiser.sfId || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color={canModify ? 'textSecondary' : 'text.disabled'}>
                      {advertiser.org_name || 'N/A'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!canModify}
                        onClick={() => handleTimezoneSelect(advertiser, 'UTC', addPlatform)}
                        sx={{
                          borderColor: canModify ? '#1976d2' : 'grey.400',
                          color: canModify ? '#1976d2' : 'grey.600',
                          fontSize: '11px',
                          fontWeight: '500',
                          cursor: canModify ? 'pointer' : 'not-allowed',
                          '&:hover': canModify
                            ? {
                                backgroundColor: '#1976d2',
                                color: 'white'
                              }
                            : {},
                          '&:disabled': {
                            opacity: 0.6
                          }
                        }}
                      >
                        {canModify
                          ? addPlatform === 'CTV'
                            ? 'Add to CTV (UTC)'
                            : addPlatform === 'GTV'
                            ? 'Add to GTV (UTC)'
                            : 'Add to DSP UTC'
                          : 'No Access'}
                      </Button>
                      {addPlatform === 'DSP' && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!canModify}
                        onClick={() => handleTimezoneSelect(advertiser, 'JST', 'DSP')}
                        sx={{
                          borderColor: canModify ? '#2e7d32' : 'grey.400',
                          color: canModify ? '#2e7d32' : 'grey.600',
                          fontSize: '11px',
                          fontWeight: '500',
                          cursor: canModify ? 'pointer' : 'not-allowed',
                          '&:hover': canModify
                            ? {
                                backgroundColor: '#2e7d32',
                                color: 'white'
                              }
                            : {},
                          '&:disabled': {
                            opacity: 0.6
                          }
                        }}
                      >
                        {canModify ? 'Add to DSP JST' : 'No Access'}
                      </Button>
                      )}
                    </Box>
                  </Box>
                );
              })
            ) : (
              <Box
                sx={{
                  p: 3,
                  textAlign: 'center',
                  color: 'text.secondary',
                  borderTop: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <Typography variant="body2">
                  {currentUserAdvertisers.length === 0
                    ? 'Loading available advertisers...'
                    : searchTerm.trim()
                    ? 'No advertisers found matching your search.'
                    : 'No available advertisers.'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Empty State */}
        {/* Removed unwanted message */}

        {/* No Advertisers Found */}
        {getCurrentUserAdvertisers().length === 0 && (
          <Box
            sx={{
              p: 3,
              bgcolor: 'error.light',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'error.main',
              textAlign: 'center'
            }}
          >
            <Typography variant="body1" color="error.dark">
              No assigned advertisers found. Please contact an administrator.
            </Typography>
          </Box>
        )}
      </Grid>
    );
  }
);

InternalAccessManager.propTypes = {
  userEmail: PropTypes.string.isRequired,
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  allUsersList: PropTypes.array.isRequired,
  advertiserMetaDetails: PropTypes.array,
  onAdvertiserAdded: PropTypes.func,
  currentAccessData: PropTypes.object,
  currentUserAdvertisers: PropTypes.array
};

InternalAccessManager.defaultProps = {
  advertiserMetaDetails: [],
  currentAccessData: {},
  currentUserAdvertisers: []
};

export default InternalAccessManager;
